import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Layouts
import DashboardLayout from '@/layouts/DashboardLayout';
import TenantLayout from '@/layouts/TenantLayout';
import ProfileLayout from '@/layouts/ProfileLayout';

// Components
import ProtectedRoute from '@/components/shared/Auth';

// Auth Pages
import Login from '@/pages/shared/Login';
import SignupPage from '@/pages/shared/Signup';
import ForgotPasswordPage from '@/pages/shared/ForgotPassword';
import ForgotChangePasswordPage from '@/pages/shared/ForgotChangePassword';
import ConfirmEmailPage from '@/pages/shared/ConfirmEmail';
import InvitePage from '@/pages/shared/Invite';

// Dashboard Pages (Owner + Manager)
import HomePage from '@/pages/dashboard/Home';
import PropertiesPage from '@/pages/dashboard/Properies';
import PropertyPage from '@/pages/dashboard/Property';
import AddPropertyPage from '@/pages/dashboard/AddProperty';
import EditPropertyNew from '@/pages/dashboard/EditPropertyNew';
import AddUnitPage from '@/pages/dashboard/AddUnit';
import EditUnitPage from '@/pages/dashboard/EditUnit';
import UnitDetailsPage from '@/pages/dashboard/UnitDetails';
import TenantsPage from '@/pages/dashboard/Tenants';
import CalendarPage from '@/pages/dashboard/Calendar';
import DashboardNotificationsPage from '@/pages/dashboard/NotificationsPage';
import VacantUnitsPage from '@/pages/dashboard/VacantUnitsPage';
import DevelopmentPage from '@/pages/dashboard/Development'; // Import DevelopmentPage

// Tenant Pages
import TenantHomePage from '@/pages/tenant/Home';
import TenantUnitDetailsPage from '@/pages/tenant/UnitDetails';
import RentedPropertiesPage from '@/pages/tenant/RentedProperties';
import RentedPropertyPage from '@/pages/tenant/RentedProperty';
import TenancyPage from '@/pages/tenant/Tenancy';
import TenantAccountPage from '@/pages/tenant/Account';
import PaymentsPage from '@/pages/tenant/Payments';
import TenantDetailPage from '@/pages/dashboard/TenantDetailPage';
import DashboardPaymentsPage from '@/pages/dashboard/PaymentsPage';
import TenantTicketsPage from '@/pages/tenant/TicketsPage';
import NotificationsPage from '@/pages/tenant/NotificationsPage'; // Import the new page
import TenantDocumentsPage from '@/pages/tenant/DocumentsPage/DocumentsPage';

// Shared Pages
import DocumentsPage from '@/pages/shared/Documents';
import AccountPage from 'pages/dashboard/Account';
import TransactionsPage from '@/pages/shared/Transactions';
import ChatsPage from '@/pages/shared/Chats';
import NotFoundPage from '@/pages/shared/NotFound';
import UnableToConnect from '@/components/shared/UnableToConnect';

// Tenant-specific pages
import SettingsPage from '@/pages/tenant/Settings';
import SupportPage from '@/pages/tenant/Support';

import { UserRole } from '@/types';
import ReportsPage from '@/pages/dashboard/Reports';
import TicketsPage from '@/pages/dashboard/TicketsPage';
import AssessmentPage from '@/pages/dashboard/Assessment';
import StatementPage from '@/pages/dashboard/Statement';
import BudgetPage from '@/pages/dashboard/Budget';
import IncomePage from '@/pages/dashboard/Income';
import ExpensesPage from '@/pages/dashboard/Expenses';
import TestPage from '@/pages/TestPage';
import TenantEditPage from '@/pages/dashboard/TenantEditPage';
import TicketEditPage from '@/pages/dashboard/TicketEditPage/TicketEditPage'; // Import TicketEditPage


const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* ============================================ */}
      {/* Публичные маршруты (без авторизации) */}
      {/* ============================================ */}
      <Route path="/login" element={<Login />} />

      <Route path="/register" element={<SignupPage />} />
      <Route path="/forgot" element={<ForgotPasswordPage />} />
      <Route path="/auth/confirm/:token" element={<ConfirmEmailPage />} />
      <Route
        path="/auth/reset-password/:token"
        element={<ForgotChangePasswordPage />}
      />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/unable-to-connect" element={<UnableToConnect />} />

      {/* ============================================ */}
      {/* Dashboard маршруты (Owner + Manager) */}
      {/* ============================================ */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={[UserRole.OWNER, UserRole.MANAGER]}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard главная */}
        <Route index element={<HomePage />} />

        {/* Аккаунт - перенесено сюда */}
        <Route path="account" element={<AccountPage />} />
        <Route path="testPage" element={<TestPage />} />

        {/* Управление недвижимостью */}

        <Route path="properties" element={<PropertiesPage />} />
        <Route path="properties/add" element={<AddPropertyPage />} />
        <Route path="properties/vacant-units" element={<VacantUnitsPage />} />
        <Route path="properties/:propertyId" element={<PropertyPage />} />
        <Route
          path="properties/:propertyId/edit"
          element={<EditPropertyNew />}
        />

        <Route path="reports" element={<ReportsPage />} />
        <Route path="tickets" element={<TicketsPage />} />
        <Route path="tickets/edit/:id" element={<TicketEditPage />} /> {/* Add new route for TicketEditPage */}
        <Route path="financial/assessment" element={<AssessmentPage />} />
        <Route path="financial/statement" element={<StatementPage />} />
        <Route path="financial/budget" element={<BudgetPage />} />
        <Route path="financial/income" element={<IncomePage />} />
        <Route path="financial/expenses" element={<ExpensesPage />} />

        <Route path="payments" element={<DashboardPaymentsPage />} />

        {/* Управление юнитами */}
        <Route
          path="properties/:propertyId/units/add"
          element={<AddUnitPage />}
        />
        <Route
          path="properties/:propertyId/units/:unitId"
          element={<UnitDetailsPage />}
        />
        <Route
          path="properties/:propertyId/units/:unitId/edit"
          element={<EditUnitPage />}
        />

        {/* Управление людьми */}
        <Route path="tenants" element={<TenantsPage />} />

        <Route path="tenants/:tenantId" element={<TenantDetailPage />} />
        <Route path="tenants/:tenantId/edit" element={<TenantEditPage />} />

        {/* Финансы */}
        <Route path="transactions" element={<TransactionsPage />} />

        {/* Коммуникации */}
        <Route path="chats" element={<ChatsPage />} />
        <Route path="notifications" element={<DashboardNotificationsPage />} />

        <Route path="calendar" element={<CalendarPage />} />
        <Route path="documents" element={<DocumentsPage />} />

        {/* Настройки и поддержка Dashboard */}
        <Route path="settings" element={<SettingsPage />} />
        <Route path="support" element={<SupportPage />} />
        
        {/* Development Page for Owner */}
        <Route
          path="development"
          element={
            <ProtectedRoute allowedRoles={[UserRole.OWNER]}>
              <DevelopmentPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* ============================================ */}
      {/* Tenant маршруты (корневые) */}
      {/* ============================================ */}
      <Route
        path="/"
        element={
          <ProtectedRoute allowedRoles={[UserRole.TENANT]}>
            <TenantLayout />
          </ProtectedRoute>
        }
      >
        {/* Главная страница tenant */}
        <Route index element={<TenantHomePage />} />

        <Route path="chats" element={<ChatsPage />} />

        {/* Tenant функционал */}
        <Route path="tenancy" element={<TenancyPage />} />
        <Route path="documents" element={<TenantDocumentsPage />} />
        <Route path="account" element={<TenantAccountPage />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="tickets" element={<TenantTicketsPage />} />
        <Route path="notifications" element={<NotificationsPage />} /> {/* Add new route */}

        {/* Недвижимость tenant */}
        <Route path="unit/:unitId" element={<TenantUnitDetailsPage />} />
        <Route path="rented" element={<RentedPropertiesPage />} />
        <Route path="rented/:unitId" element={<RentedPropertyPage />} />

        {/* Коммуникации tenant */}
        <Route path="communication" element={<ChatsPage />} />

        {/* Настройки и поддержка Tenant */}
        <Route path="settings" element={<SettingsPage />} />
        <Route path="support" element={<SupportPage />} />

        {/* Сервисы tenant */}
        <Route path="services" element={<div>Заказ услуг (TODO)</div>} />

        {/* Управление менеджерами (только Owner) - доступно через /dashboard/managers */}
      </Route>

      {/* ============================================ */}
      {/* Маршруты только для Owner */}
      {/* ============================================ */}
      <Route
        path="/dashboard/managers"
        element={
          <ProtectedRoute allowedRoles={[UserRole.OWNER]}>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<div>Управление менеджерами (TODO)</div>} />
      </Route>

      {/* ============================================ */}
      {/* Редиректы для совместимости */}
      {/* ============================================ */}
      <Route
        path="/account"
        element={<Navigate to="/dashboard/account" replace />}
      />
      <Route
        path="/properties"
        element={<Navigate to="/dashboard/properties" replace />}
      />
      <Route
        path="/tenants"
        element={<Navigate to="/dashboard/tenants" replace />}
      />

      {/* ============================================ */}
      {/* 404 страница */}
      {/* ============================================ */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;
